/**
 * Workshop hook for drag-to-resize on frame shapes.
 *
 * Returns { width, height, onResizeStart } — wire onResizeStart into
 * FrameFooter's resize handle, and apply width/height to the wrapper div.
 */

import { useState, useCallback, useRef } from "react";

interface UseFrameResizeOptions {
  /** Initial width in pixels */
  initialWidth: number;
  /** Initial height in pixels (undefined = auto) */
  initialHeight?: number;
  /** Minimum width */
  minWidth?: number;
  /** Minimum height */
  minHeight?: number;
}

interface UseFrameResizeReturn {
  width: number;
  height: number | undefined;
  onResizeStart: React.MouseEventHandler;
}

export function useFrameResize({
  initialWidth,
  initialHeight,
  minWidth = 180,
  minHeight = 80,
}: UseFrameResizeOptions): UseFrameResizeReturn {
  const [width, setWidth] = useState(initialWidth);
  const [height, setHeight] = useState<number | undefined>(initialHeight);
  const startRef = useRef({ x: 0, y: 0, w: 0, h: 0 });

  const onResizeStart: React.MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startY = e.clientY;
      startRef.current = { x: startX, y: startY, w: width, h: height ?? 0 };

      const hasHeight = height !== undefined;

      const onMouseMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startRef.current.x;
        const dy = ev.clientY - startRef.current.y;
        setWidth(Math.max(minWidth, startRef.current.w + dx));
        if (hasHeight) {
          setHeight(Math.max(minHeight, startRef.current.h + dy));
        }
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [width, height, minWidth, minHeight],
  );

  return { width, height, onResizeStart };
}
